/**
 * card-add-to-cart.js
 *
 * Intercepts plain /cart/add form submissions from product cards.
 * Uses Fetch API to add to cart silently, then:
 *   1. Updates cart count in both headers (cart-icon + bb-cart-badge)
 *   2. Refreshes cart drawer contents via cart:update event
 *   3. Opens the cart drawer
 *
 * Works on: product-card-standard, new-launches, recommended-products,
 *           product-list, and any other plain /cart/add form NOT inside
 *           a <product-form-component> (which has its own handler).
 */

(function () {
  'use strict';

  // ── Open cart drawer ──────────────────────────────────────────────────────

  function openCartDrawer() {
    // Primary: global exposed by baybee-cart-drawer.liquid
    if (typeof window.openCartDrawer === 'function') {
      window.openCartDrawer();
      return;
    }
    // Secondary: custom element method
    var comp = document.querySelector('cart-drawer-component');
    if (comp) {
      if (typeof comp.open === 'function') { comp.open(); return; }
      if (typeof comp.showDialog === 'function') { comp.showDialog(); return; }
    }
    // Fallback: dialog element
    var dlg = document.querySelector('.cart-drawer__dialog');
    if (dlg && !dlg.open) { dlg.showModal(); }
  }

  // ── Update cart counts ────────────────────────────────────────────────────

  function updateAllCartCounts(itemCount) {
    // 1. baybee-header custom badge (.bb-cart-badge)
    document.querySelectorAll('.bb-cart-badge').forEach(function (el) {
      el.textContent = itemCount;
      el.classList.toggle('bb-hidden', itemCount === 0);
    });

    // 2. cart-icon component — dispatch cart:update with itemCount
    //    cart-icon.js reads event.detail.data.itemCount
    //    baybee-header.js reads event.detail.itemCount OR event.detail.data.itemCount
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        itemCount: itemCount,
        data: {
          itemCount: itemCount,
          source: 'card-add-to-cart'
        }
      }
    }));
  }

  // ── Button states ─────────────────────────────────────────────────────────

  function setLoading(btn) {
    if (!btn) return;
    btn.disabled = true;
    btn.dataset.originalText = btn.textContent.trim();
    btn.textContent = 'Adding...';
  }

  function setSuccess(btn) {
    if (!btn) return;
    var original = btn.dataset.originalText || 'Add to Cart';
    btn.textContent = 'Added \u2713';
    setTimeout(function () {
      btn.textContent = original;
      btn.disabled = false;
    }, 1500);
  }

  function setError(btn) {
    if (!btn) return;
    btn.textContent = btn.dataset.originalText || 'Add to Cart';
    btn.disabled = false;
  }

  // ── Main submit handler ───────────────────────────────────────────────────

  function handleSubmit(event) {
    var form = event.target;

    // Only handle /cart/add forms
    if (!form || !form.action) return;
    if (form.action.indexOf('/cart/add') === -1) return;

    // Skip forms already handled by product-form-component
    if (form.closest('product-form-component')) return;

    // Skip if no variant ID
    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) return;

    event.preventDefault();
    event.stopPropagation();

    var btn = form.querySelector('button[type="submit"]');
    setLoading(btn);

    var formData = new FormData(form);
    formData.delete('return_to');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    })
    .then(function (response) {
      if (!response.ok) {
        return response.json().then(function (err) {
          throw new Error(err.description || 'Could not add to cart');
        });
      }
      return response.json();
    })
    .then(function () {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      setSuccess(btn);
      updateAllCartCounts(cart.item_count);

      // Open drawer after counts update
      setTimeout(function () {
        openCartDrawer();
      }, 150);
    })
    .catch(function (err) {
      setError(btn);
      if (window.console) {
        console.warn('[card-add-to-cart] Failed:', err.message);
      }
    });
  }

  // Single delegated listener handles all current and future product cards
  document.addEventListener('submit', handleSubmit, true);

})();
